import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Alert } from '../components/Field';
import { PreviewModal } from '../components/PreviewModal';
import { duration, LESSON_ICON, money, titleCase } from '../lib/format';

interface Lesson {
  id: string;
  title: string;
  type: string;
  durationMinutes: number;
  isRequired: boolean;
  isFreePreview: boolean;
}

interface CourseDetailModel {
  id: string;
  title: string;
  slug: string;
  shortDescription: string;
  longDescription: string | null;
  targetAudience: string | null;
  difficulty: string;
  language: string;
  durationMinutes: number;
  priceMinor: number;
  currency: string;
  isFree: boolean;
  rating: { average: number; count: number };
  enrolledCount: number;
  category: { name: string; slug: string };
  thumbnailUrl: string | null;
  objectives: string[];
  skills: Array<{ name: string; slug: string }>;
  instructors: Array<{ name: string; slug: string; headline: string | null }>;
  prerequisites: Array<{ title: string; slug: string; enforcement: string }>;
  curriculum: Array<{ title: string; description: string | null; lessons: Lesson[] }>;
  totals: { modules: number; lessons: number; freePreviewLessons: number };
  reviews: Array<{ rating: number; title: string | null; body: string | null; createdAt: string }>;
}

export function CourseDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [course, setCourse] = useState<CourseDetailModel | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [open, setOpen] = useState<Set<number>>(new Set([0]));
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    setCourse(null);
    api<CourseDetailModel>(`/courses/${slug}`)
      .then(setCourse)
      .catch((caught) => setError(caught as ApiError));
  }, [slug]);

  if (error) {
    return (
      <div className="wrap" style={{ padding: '40px 22px' }}>
        <Alert kind="error" title={error.message} />
        <Link to="/catalog">← Back to the catalog</Link>
      </div>
    );
  }
  if (!course) return <div className="spinner">Loading course…</div>;

  function toggle(index: number) {
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  return (
    <>
      <section className="cdetail__hero">
        <div className="wrap">
          <div className="cdetail__grid">
            <div>
              <p className="cdetail__crumb">
                <Link to="/catalog">Courses</Link> ·{' '}
                <Link to={`/catalog?category=${course.category.slug}`}>{course.category.name}</Link>
              </p>
              <h1 className="cdetail__title">{course.title}</h1>
              <p className="cdetail__sub">{course.shortDescription}</p>

              <div className="cdetail__facts">
                <span>
                  <strong>{titleCase(course.difficulty)}</strong> level
                </span>
                <span>
                  <strong>{duration(course.durationMinutes)}</strong> of content
                </span>
                <span>
                  <strong>{course.totals.lessons}</strong> lessons in{' '}
                  <strong>{course.totals.modules}</strong> modules
                </span>
                {course.rating.count > 0 ? (
                  <span>
                    <strong>★ {course.rating.average.toFixed(1)}</strong> ({course.rating.count})
                  </span>
                ) : null}
                {course.enrolledCount > 0 ? (
                  <span>
                    <strong>{course.enrolledCount.toLocaleString()}</strong> enrolled
                  </span>
                ) : null}
                <span>
                  Language: <strong>{course.language.toUpperCase()}</strong>
                </span>
              </div>

              {course.instructors.length > 0 ? (
                <p className="cdetail__facts" style={{ marginTop: 14 }}>
                  <span>
                    Taught by <strong>{course.instructors.map((i) => i.name).join(', ')}</strong>
                  </span>
                </p>
              ) : null}
            
              {course.skills.length > 0 ? (
                <div className="cdetail__skills">
                  <span className="cdetail__skills__label">Skills you will build</span>
                  <span className="cdetail__skills__list">
                    {course.skills.map((skill) => (
                      <Link
                        className="pill pill--role"
                        key={skill.slug}
                        to={`/catalog?skill=${skill.slug}`}
                      >
                        {skill.name}
                      </Link>
                    ))}
                  </span>
                </div>
              ) : null}
</div>

            <aside className="buybox">
              <div className="buybox__media">
                {course.thumbnailUrl ? <img src={course.thumbnailUrl} alt="" /> : null}
              </div>
              <div className="buybox__body">
                <div className="buybox__price">
                  {course.isFree ? 'Free' : money(course.priceMinor, course.currency)}
                  {!course.isFree ? <small> one-time</small> : null}
                </div>

                <EnrolAction course={course} />

                <ul className="buybox__list">
                  <li>
                    <span>▤</span> {course.totals.lessons} lessons · {duration(course.durationMinutes)}
                  </li>
                  {course.totals.freePreviewLessons > 0 ? (
                    <li>
                      <span>▶</span> {course.totals.freePreviewLessons} free preview
                      {course.totals.freePreviewLessons === 1 ? '' : 's'}
                    </li>
                  ) : null}
                  <li>
                    <span>✦</span> Certificate on completion
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <div className="wrap cdetail__body">
        <div className="cdetail__grid">
          <div>
            {course.objectives.length > 0 ? (
              <section className="section">
                <h2 className="section__title">What you will learn</h2>
                <ul className="objectives">
                  {course.objectives.map((objective) => (
                    <li key={objective}>
                      <span className="tick">✓</span>
                      {objective}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <section className="section">
              <h2 className="section__title">Course content</h2>
              <p className="muted" style={{ marginTop: -6, marginBottom: 12, fontSize: 13 }}>
                {course.totals.modules} modules · {course.totals.lessons} lessons ·{' '}
                {duration(course.durationMinutes)}
              </p>

              <div className="curriculum">
                {course.curriculum.map((module, index) => (
                  <div className="curriculum__mod" key={module.title}>
                    <button className="curriculum__head" onClick={() => toggle(index)}>
                      <span>
                        <span className="curriculum__modtitle">
                          {open.has(index) ? '▾' : '▸'} {module.title}
                        </span>
                      </span>
                      <span className="curriculum__modmeta">
                        {module.lessons.length} lesson{module.lessons.length === 1 ? '' : 's'}
                      </span>
                    </button>

                    {open.has(index) && module.description ? (
                      <p className="curriculum__moddesc">{module.description}</p>
                    ) : null}

                    {open.has(index)
                      ? module.lessons.map((lesson) => (
                          <div className="curriculum__lesson" key={lesson.id}>
                            <span className="curriculum__type" aria-hidden="true">
                              {LESSON_ICON[lesson.type] ?? '•'}
                            </span>
                            <span className="curriculum__name">
                              {lesson.title}
                              {!lesson.isRequired ? <span className="muted"> · optional</span> : null}
                            </span>
                            {lesson.isFreePreview ? (
                              <button className="preview-pill" onClick={() => setPreview(lesson.id)}>
                                Preview
                              </button>
                            ) : null}
                            <span className="curriculum__dur">{duration(lesson.durationMinutes)}</span>
                          </div>
                        ))
                      : null}
                  </div>
                ))}
              </div>
            </section>

            {course.longDescription ? (
              <section className="section">
                <h2 className="section__title">About this course</h2>
                <div className="prose">
                  {course.longDescription.split('\n').map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                </div>
              </section>
            ) : null}

            {course.prerequisites.length > 0 ? (
              <section className="section">
                <h2 className="section__title">Prerequisites</h2>
                <ul className="objectives">
                  {course.prerequisites.map((prerequisite) => (
                    <li key={prerequisite.slug}>
                      <span className="tick">•</span>
                      <span>
                        <Link to={`/courses/${prerequisite.slug}`}>{prerequisite.title}</Link>
                        {prerequisite.enforcement === 'BLOCKING' ? (
                          <span className="muted"> — required before enrolling</span>
                        ) : (
                          <span className="muted"> — recommended</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {course.targetAudience ? (
              <section className="section">
                <h2 className="section__title">Who should take this course?</h2>
                <div className="prose">
                  <p>{course.targetAudience}</p>
                </div>
              </section>
            ) : null}

            {course.reviews.length > 0 ? (
              <section className="section">
                <h2 className="section__title">Learner reviews</h2>
                {course.reviews.map((review, index) => (
                  <div className="card" key={index} style={{ marginBottom: 10 }}>
                    <div className="card__body">
                      {/* A row of its own with no gap, so filled and empty stars
                          read as one set of five and the title starts below. */}
                      <div
                        className="rating"
                        role="img"
                        aria-label={`${review.rating} out of 5 stars`}
                        style={{ display: 'flex', gap: 0, marginBottom: 6 }}
                      >
                        <span className="rating__star">{'★'.repeat(review.rating)}</span>
                        <span className="muted">{'★'.repeat(5 - review.rating)}</span>
                      </div>
                      {review.title ? <strong>{review.title}</strong> : null}
                      {review.body ? (
                        <p style={{ margin: '6px 0 0', fontSize: 14, color: 'var(--ink-soft)' }}>
                          {review.body}
                        </p>
                      ) : null}
                    </div>
                  </div>
                ))}
              </section>
            ) : null}
          </div>

          <aside>
            {course.instructors.length > 0 ? (
              <div className="card">
                <div className="card__head">
                  <span className="card__title">Instructor</span>
                </div>
                <div className="card__body">
                  {course.instructors.map((instructor) => (
                    <div key={instructor.slug} style={{ marginBottom: 10 }}>
                      <strong>{instructor.name}</strong>
                      {instructor.headline ? (
                        <p style={{ margin: '3px 0 0', fontSize: 13, color: 'var(--ink-soft)' }}>
                          {instructor.headline}
                        </p>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </aside>
        </div>
      </div>

      {preview ? <PreviewModal lessonId={preview} onClose={() => setPreview(null)} /> : null}
    </>
  );
}

/**
 * Enrol, or go straight to the player.
 *
 * The button never predicts the answer: it calls the API and shows whatever
 * the entitlement engine says. A paid course the visitor has not bought comes
 * back refused, with the engine's own message.
 */
function EnrolAction({ course }: { course: CourseDetailModel }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  // Someone already on the course is offered the course, not a second
  // enrolment: "Enrol free" to a learner halfway through it reads as if their
  // place had been lost. Withdrawn enrolments are not in this list.
  const [enrolled, setEnrolled] = useState(false);

  useEffect(() => {
    if (!user) return;
    let live = true;
    api<{ items: Array<{ course: { id: string } }> }>('/my/courses?filter=all')
      .then((result) => {
        if (live) setEnrolled(result.items.some((item) => item.course.id === course.id));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [user, course.id]);

  if (enrolled) {
    return (
      <Link className="btn btn--block" style={{ marginTop: 14 }} to={`/learn/${course.id}`}>
        Go to course
      </Link>
    );
  }

  async function enrol() {
    setBusy(true);
    setRefusal(null);
    try {
      await api('/enrollments', {
        method: 'POST',
        body: JSON.stringify({ courseId: course.id }),
      });
      navigate(`/learn/${course.id}`);
    } catch (caught) {
      const error = caught as ApiError;
      // A visitor who is not signed in is sent to sign in, not told off.
      if (error.status === 401) {
        navigate('/login', { state: { from: `/courses/${course.slug}` } });
        return;
      }
      // A paid course they have not bought: that is a purchase, not a refusal.
      if (error.code === 'ENTITLEMENT_REQUIRED' && !course.isFree) {
        navigate(`/checkout/${course.slug}`);
        return;
      }
      setRefusal(error.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        className="btn btn--block"
        style={{ marginTop: 14 }}
        disabled={busy}
        onClick={() => void enrol()}
      >
        {busy ? 'One moment…' : course.isFree ? 'Enrol free' : 'Buy now'}
      </button>
      {refusal ? (
        <p
          style={{
            font: 'var(--sx-body-sm)',
            color: 'var(--sx-warning-700)',
            background: 'var(--sx-warning-subtle)',
            borderRadius: 'var(--sx-radius-sm)',
            padding: 'var(--sx-space-2) var(--sx-space-3)',
            marginTop: 'var(--sx-space-3)',
          }}
        >
          {refusal}
        </p>
      ) : null}
    </>
  );
}
